import { useState, useEffect } from 'react';
import {
  MdExpandMore,
  MdExpandLess,
  MdDescription,
  MdDelete,
  MdRefresh,
  MdClose,
  MdInfo
} from 'react-icons/md';
import { lessonAPI } from '../../utils/api';

const ExpandMore = MdExpandMore as any;
const ExpandLess = MdExpandLess as any;
const Description = MdDescription as any;
const Delete = MdDelete as any;
const Refresh = MdRefresh as any;
const X = MdClose as any;
const Info = MdInfo as any;

interface Material {
  id: string;
  file_name: string;
  uploaded_at: string | null;
  upload_order: number;
}

interface LessonContextData {
  lesson_id: string;
  lesson_context: string | null;
  context_generated_at: string | null;
  materials: Material[];
  material_count: number;
}

interface LessonContextPanelProps {
  lessonId: string;
  onContextUpdate?: () => void;
}

export default function LessonContextPanel({
  lessonId,
  onContextUpdate
}: LessonContextPanelProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [contextData, setContextData] = useState<LessonContextData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deletingMaterialId, setDeletingMaterialId] = useState<string | null>(null);

  const loadContext = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await lessonAPI.getLessonContext(lessonId);
      setContextData(data);
    } catch (err: any) {
      console.error('Error loading lesson context:', err);
      setError(err.message || 'Failed to load lesson context');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isExpanded) {
      loadContext();
    }
  }, [isExpanded, lessonId]);

  const handleDeleteMaterial = async (materialId: string) => {
    if (!window.confirm('Are you sure you want to remove this material? The lesson context will be regenerated.')) {
      return;
    }

    setDeletingMaterialId(materialId);
    setError(null);

    try {
      await lessonAPI.deleteMaterial(lessonId, materialId);
      await loadContext(); // Reload to get updated context
      onContextUpdate?.();
    } catch (err: any) {
      console.error('Error deleting material:', err);
      setError(err.message || 'Failed to delete material');
    } finally {
      setDeletingMaterialId(null);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Unknown';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return 'Unknown';
    }
  };

  const hasContext = contextData && contextData.lesson_context;

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm" data-tour="lesson-context-panel">
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Description className="h-5 w-5 text-blue-600" />
          <span className="font-medium text-gray-900">
            Lesson Context
            {contextData && contextData.material_count > 0 && (
              <span className="ml-2 text-sm text-gray-500">
                ({contextData.material_count} {contextData.material_count === 1 ? 'file' : 'files'})
              </span>
            )}
          </span>
        </div>
        {isExpanded ? (
          <ExpandLess className="h-5 w-5 text-gray-600" />
        ) : (
          <ExpandMore className="h-5 w-5 text-gray-600" />
        )}
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="border-t border-gray-200 p-4 space-y-4">
          {/* Loading State */}
          {isLoading && (
            <div className="text-center py-8">
              <Refresh className="h-8 w-8 text-gray-400 animate-spin mx-auto mb-2" />
              <p className="text-sm text-gray-500">Loading context...</p>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              <Info className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium">Error</p>
                <p>{error}</p>
              </div>
              <button
                onClick={() => setError(null)}
                className="p-1 hover:bg-red-100 rounded transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}

          {/* No Context State */}
          {!isLoading && !hasContext && !error && (
            <div className="text-center py-8">
              <Description className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-600 mb-1">No lesson context yet</p>
              <p className="text-xs text-gray-500">
                Upload PDF materials to establish context for this lesson
              </p>
            </div>
          )}

          {/* Context Display */}
          {!isLoading && hasContext && (
            <>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium text-gray-700">Context Summary</h4>
                  {contextData.context_generated_at && (
                    <span className="text-xs text-gray-500">
                      Updated {formatDate(contextData.context_generated_at)}
                    </span>
                  )}
                </div>
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">
                    {contextData.lesson_context}
                  </p>
                </div>
              </div>

              {/* Materials List */}
              {contextData.materials && contextData.materials.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-gray-700">
                    Source Materials
                  </h4>
                  <div className="space-y-2">
                    {contextData.materials.map((material) => (
                      <div
                        key={material.id}
                        className="flex items-center justify-between p-2 bg-gray-50 rounded border border-gray-200"
                      >
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <Description className="h-4 w-4 text-red-600 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-gray-700 truncate">
                              {material.file_name}
                            </p>
                            {material.uploaded_at && (
                              <p className="text-xs text-gray-500">
                                {formatDate(material.uploaded_at)}
                              </p>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => handleDeleteMaterial(material.id)}
                          disabled={deletingMaterialId === material.id}
                          className="ml-2 p-1.5 hover:bg-red-100 text-red-600 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          aria-label="Delete material"
                          title="Remove this material"
                        >
                          {deletingMaterialId === material.id ? (
                            <Refresh className="h-4 w-4 animate-spin" />
                          ) : (
                            <Delete className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Refresh Button */}
              <button
                onClick={loadContext}
                className="w-full py-2 px-4 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                <Refresh className="h-4 w-4" />
                Refresh Context
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
